import BaseModal from "../../../components/ui/BaseModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { CurrencySymbol, UserType } from "../../../utils/enum";
import { useFormik } from "formik";
import * as yup from "yup";
import { useState } from "react";
import {
  createInvoice,
  sendCustomerInvoice,
  sendEngineerInvoice,
  // updateInvoiceStatus,
} from "../../../services/api/invoice";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useNavigate } from "react-router-dom";

const validationSchema = (sendEmail) =>
  yup.object({
    billingToEmail: sendEmail
      ? yup.string().required("Company Email is required")
      : yup.string().nullable(),
    ccEmails: yup
    .string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .test("comma-separated-emails", "Enter valid comma-separated emails", (value) => {
      if (!value) return true; // empty is allowed
      const emails = value.split(",").map((e) => e.trim());
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emails.every((email) => emailRegex.test(email));
    }),
});

const SendInvoiceModal = ({
  open,
  close,
  billingToEmail,
  invoiceId,
  refetchInvoiceDetails,
  role,
}) => {
  const dispatch = useDispatch();

  const {
    isPending: isSendCustomerInvoiceLoadingApi,
    error: isSendCustomerInvoiceErrorApi,
    mutateAsync: sendCustomerInvoiceMutation,
  } = useMutation({
    mutationFn: ({ id, status, toEmail, ccEmails, skipEmail }) =>
      sendCustomerInvoice(id, { status, toEmail, ccEmails, skipEmail }),
    enabled: !!invoiceId,
  });

  const {
    isPending: isSendEngineerInvoiceLoadingApi,
    error: isSendEngineerInvoiceErrorApi,
    mutateAsync: sendEngineerInvoiceMutation,
  } = useMutation({
    mutationFn: ({ id, status, toEmail, ccEmails, skipEmail }) =>
      sendEngineerInvoice(id, { status, toEmail, ccEmails, skipEmail }),
    enabled: !!invoiceId,
  });

  const [sendEmail, setSendEmail] = useState(true);

  const formik = useFormik({
    initialValues: {
      billingToEmail: billingToEmail,
      ccEmails: "",
    },
    validationSchema: validationSchema(sendEmail),
    onSubmit: (values, { resetForm }) => {
      handleUpdateInvoiceStatus("UNPAID");
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleUpdateInvoiceStatus = async (status) => {
    try {
      if (role === UserType.CLIENT) {
        await sendCustomerInvoiceMutation({
          id: Number(invoiceId),
          status: status,
          toEmail: sendEmail ? billingToEmail : undefined,
          ccEmails: formik.values.ccEmails,
          skipEmail: !sendEmail,
        });
      } else {
        await sendEngineerInvoiceMutation({
          id: Number(invoiceId),
          status: status,
          toEmail: sendEmail ? billingToEmail : undefined,
          ccEmails: formik.values.ccEmails,
          skipEmail: !sendEmail,
        });
      }

      refetchInvoiceDetails();
      close(false);
    } catch (err) {
      dispatch(
        showToast({
          type: "error",
          message: err.response.data.message,
        }),
      );
    }
  };

  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open={open}
      close={() => {
        close(false);

        // formik.resetForm();
      }}
    >
      <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
        <h6 className="text-dark font-normal border-b pb-1">Send Invoice</h6>

        <div className="grid grid-cols-1 gap-x-10 gap-y-4 mt-4">
          <BaseTextField
            disabled={true}
            startAdornment={<i class="fa-solid fa-envelope"></i>}
            label="Company Email"
            isDefaultField={false}
            labelShrink={false}
            optional={false}
            id="billingToEmail"
            name="billingToEmail"
            type="email"
            value={formik.values.billingToEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.billingToEmail && Boolean(formik.errors.billingToEmail)}
            helperText={formik.touched.billingToEmail && formik.errors.billingToEmail}
          />

          <BaseTextField
            hint={"(Enter comma-separated emails only)"}
            label="Cc"
            placeholder="e.g. john@example.com, sarah@gmail.com, test@outlook.com"
            startAdornment={<i class="fa-solid fa-envelope"></i>}
            isDefaultField={false}
            labelShrink={false}
            optional={false}
            id="ccEmails"
            name="ccEmails"
            value={formik.values.ccEmails}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.ccEmails && Boolean(formik.errors.ccEmails)}
            helperText={formik.touched.ccEmails && formik.errors.ccEmails}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendEmail"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 accent-primary cursor-pointer"
            />
            <label htmlFor="sendEmail" className="text-sm text-dark cursor-pointer select-none">
              {role === UserType.CLIENT ? "Send email to client" : "Send email to tech"}
            </label>
          </div>
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6 mt-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className=" px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => {
            close(false);
            /* setSelectedEmployment(null);
              formik.resetForm(); */
          }}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isSendCustomerInvoiceLoadingApi || isSendEngineerInvoiceLoadingApi}
          disabled={!formik.isValid}
          loaderSize={24}
          loaderColor="primary"
          className="px-[20px] py-[5px]"
          color="primary"
          onClick={formik.handleSubmit}
        >
          Send Invoice
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default SendInvoiceModal;
